import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { FileHandlerBaseService } from '@shared/services/file-handler/file-handler-base.service';
import {
  CUSTOMER_REPORTING_ACTIONS,
  CustomerReportingDataService,
  ICallerInformation,
  IReportFilters,
  ReportStoredProcedure,
} from '@usf/ngrx-customer-reporting';
import { firstValueFrom, Observable } from 'rxjs';
import { Capacitor } from '@capacitor/core';
import { Filesystem, Directory, Encoding } from '@capacitor/filesystem';
import { PLATFORM_CAPACITOR_TYPE } from '@shared/constants/platform-capacitor';
import {
  CONTENT_TYPE_CSV,
  CONTENT_TYPE_CSV_ANDROID,
} from '@shared/constants/files';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root',
})
export class CustomerReportingCsvService {
  constructor(
    private fileHandlerBaseService: FileHandlerBaseService,
    private dataService: CustomerReportingDataService,
    private store: Store,
    private http: HttpClient,
  ) {}

  async downloadCsv(
    callerInfo: ICallerInformation,
    report: ReportStoredProcedure,
    filters: IReportFilters,
  ): Promise<void> {
    this.store.dispatch(
      CUSTOMER_REPORTING_ACTIONS.loadCsvCustomerReport({ report }),
    );

    const response = await firstValueFrom(
      this.dataService.getCustomerReportCsv(callerInfo, report, filters),
    );

    try {
      if (response.status === 204 || !response.body) {
        this.store.dispatch(
          CUSTOMER_REPORTING_ACTIONS.loadCsvCustomerReportFailed({
            error: 'NO_DATA',
            report,
          }),
        );
        return;
      }

      const contentDisposition = response.headers.get('Content-Disposition');
      const filename =
        this.getFilenameFromContentDisposition(contentDisposition);
      const blob = response.body;
      const reader = blob.stream().getReader();
      const decoder = new TextDecoder('utf-8');

      if (Capacitor.isNativePlatform()) {
        try {
          const platform = Capacitor.getPlatform();
          const directory =
            platform === PLATFORM_CAPACITOR_TYPE.ANDROID
              ? Directory.Data
              : Directory.Documents;

          await Filesystem.deleteFile({
            path: filename,
            directory: directory,
          }).catch(() => {});

          const fileResult = await Filesystem.writeFile({
            path: filename,
            data: '',
            directory: directory,
            encoding: Encoding.UTF8,
          });

          const processChunk = async ({
            done,
            value,
          }: ReadableStreamReadResult<Uint8Array>): Promise<void> => {
            if (done) {
              this.store.dispatch(
                CUSTOMER_REPORTING_ACTIONS.loadCsvCustomerReportSuccess({
                  report,
                }),
              );

              try {
                await this.fileHandlerBaseService.openFileNative(
                  fileResult,
                  platform === PLATFORM_CAPACITOR_TYPE.ANDROID
                    ? CONTENT_TYPE_CSV_ANDROID
                    : CONTENT_TYPE_CSV,
                  platform === PLATFORM_CAPACITOR_TYPE.ANDROID,
                );
              } catch (error) {
                alert(JSON.stringify(error));
              }

              return;
            }

            const chunkText = decoder.decode(value, { stream: true });

            await Filesystem.appendFile({
              path: fileResult.uri,
              data: chunkText,
              encoding: Encoding.UTF8,
            });

            return reader.read().then(processChunk);
          };

          reader.read().then(processChunk);
        } catch (error) {
          alert(error);
        }
      } else {
        let csvData = '';

        const processText = ({ done, value }): Promise<void> => {
          if (done) {
            const csvBlob = new Blob([csvData], {
              type: 'text/csv;charset=utf-8;',
            });
            this.generateDownload(csvBlob, filename, report);
            return Promise.resolve();
          }

          csvData += decoder.decode(value, { stream: true });
          return reader.read().then(processText);
        };

        console.log('Starting stream read');
        reader.read().then(processText);
      }
    } catch (error) {
      this.store.dispatch(
        CUSTOMER_REPORTING_ACTIONS.loadCsvCustomerReportFailed({
          report: 'csv',
          error,
        }),
      );
    }
  }

  // Placeholder method to fill table, once filter functionality is implemented, this will be removed
  getCsvAsJson(): Observable<any> {
    return this.http.post(
      `${environment.customerReportingApiUrl}reports/json`,
      {
        callerInfo: {
          firstName: 'Table',
          lastName: 'View',
          userName: 'TABLEVIEW',
        },
        reportInfo: {
          reportName: 'SP_QUERY_PRODUCT_DETAIL_DATA',
          filters: {
            divisions: [{ divisionNumber: 1054, customers: [61278792] }],
            invoiceDate: {
              start: '2024-06-18',
              end: '2024-06-22',
            },
            limit: 10,
            sortBy: 'Dollars',
          },
        },
      },
    );
  }

  getFilenameFromContentDisposition(
    contentDisposition: string | null,
  ): string | null {
    if (!contentDisposition) return null;
    const matches = /filename="([^"]+)"/.exec(contentDisposition);
    return matches ? matches[1] : null;
  }

  async generateDownload(
    csvData: Blob,
    fileName: string,
    report: string,
  ): Promise<void> {
    const blob = new Blob([csvData], { type: 'text/csv' });

    try {
      await this.fileHandlerBaseService.downloadFile(
        blob,
        fileName,
        'text/csv',
      );
      this.store.dispatch(
        CUSTOMER_REPORTING_ACTIONS.loadCsvCustomerReportSuccess({ report }),
      );
    } catch (error) {
      this.store.dispatch(
        CUSTOMER_REPORTING_ACTIONS.loadCsvCustomerReportFailed({
          report: 'csv',
          error,
        }),
      );
    }
  }
}
